import { useEffect, useMemo } from "react";
import { postSearchHotelsService, postInitBookingService } from "api.service";
import { useSearch } from "context/SearchContext";
import { useSearchResultFn } from "hooks/useSearchApi";
import { ISearchResult } from "interfaces/searchResult";
import { IPostInitBookingServiceParams } from "interfaces/booking";
import { currentUrlParams } from "utils/getUrlParams";
import { calculateStayTotalPrice } from "utils/prices.utils";
import moment from "moment";
import { StepProps } from "components/Stepper/StepperTypes";
import BookingRooms from "containers/ListingDetailPage/BookingRooms";
import { useFetchHotelDetails } from "hooks/useFetchHotelDetails";
import { useParams, useHistory } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { DisplaySideBarBody } from "containers/ListingDetailPage/DisplaySideBarBody";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { BookingSummary } from "./BookingSummary";
dayjs.locale("fr");

export const Step2: React.FC<StepProps> = ({
  handleStepChange,
  currentStep,
  selectedArrangements,
  setSelectedArrangements,
  selectedRooms,
  setSelectedRooms,
}) => {
  const hotelService = useSearchResultFn(postSearchHotelsService);
  const initBookingService = useSearchResultFn(postInitBookingService);
  const { hotelSlug }: { hotelSlug: string } = useParams();
  const history = useHistory();
  const { searchData, setSearchData, searchResult, setSearchResult, userType } =
    useSearch();
  const { hotelDetails } = useFetchHotelDetails();
  const currentParams = currentUrlParams();

  useEffect(() => {
    if (searchResult?.occupancies.length !== 0) {
      const rooms = searchResult?.occupancies.reduce(
        (acc: any, val: any, index: any) => {
          const sortedByPrice = val.room_types.sort(
            (a: any, b: any) =>
              Number(a?.board?.[0].net) - Number(b?.board?.[0].net)
          );
          const firstSelectableRoomForSales = sortedByPrice.find(
            (el: any) => !el.stop_sale && el.quantity
          );
          const firstSelectableRoomOverbooked = sortedByPrice.find(
            (el: any) => !el.stop_sale && !el.quantity
          );
          const firstSelectableRoomStopsales = sortedByPrice.find(
            (el: any) => el.stop_sale
          );
          acc = {
            ...acc,
            [index]:
              firstSelectableRoomForSales?.data.id ||
              firstSelectableRoomOverbooked?.data.id ||
              firstSelectableRoomStopsales?.data.id,
          };
          return acc;
        },
        {}
      );
      setSelectedRooms(rooms);

      const arrangements = searchResult?.occupancies.reduce(
        (acc: any, val: any, index: any) => {
          acc = {
            ...acc,
            [index]: val.room_types.reduce((acc: any, roomType: any) => {
              acc = {
                ...acc,
                [roomType.data.id]: { boardId: roomType.board[0].data.id },
              };
              return acc;
            }, {}),
          };
          return acc;
        },
        {}
      );
      setSelectedArrangements(arrangements);
    }
  }, [searchResult]);

  useEffect(() => {
    if (currentParams.start) {
      const start_date = moment(currentParams.start);
      const end_date = moment(currentParams.end);
      const roomCount = Number(currentParams.rooms);
      const occupancies = [];
      for (let i = 1; i <= roomCount; i++) {
        const adults = Number(currentParams[`adults${i}`] || 2);
        const childrenParam = currentParams[`children${i}`];
        const child_ages = childrenParam
          ? childrenParam
              .split(",")
              .map(Number)
              .filter((age: number) => !isNaN(age))
          : [];
        occupancies.push({
          adult: adults,
          child: child_ages.length,
          child_ages: child_ages,
        });
      }
      const rooms = occupancies.map((occ) => ({
        guestAdults: occ.adult,
        guestChildren: occ.child_ages,
      }));
      setSearchData((prev: any) => ({
        ...prev,
        dateRange: { startDate: start_date, endDate: end_date },
        rooms,
      }));

      hotelService
        .execute({
          hotel: hotelSlug,
          start_date: start_date.format("YYYY-MM-DD"),
          end_date: end_date.format("YYYY-MM-DD"),
          currency: searchData?.currency,
          occupancies,
        })
        .then((res: [ISearchResult]) => {
          setSearchResult(res[0]);
          setTimeout(() => {
            document
              .getElementById("search-room-result")
              ?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        });
    }
  }, []);

  const isStopSale = useMemo(() => {
    return searchResult?.occupancies.reduce(
      (acc: any, val: any, index: any) => {
        const room = val.room_types.find(
          (el: any) => el.data.id === selectedRooms[index]
        );
        if (acc) return acc;
        if (!room) return true;
        if (room.rate_type === "stop_sale") return true;
        if (room?.stop_sale) return true;
        return false;
      },
      false
    );
  }, [searchResult, selectedRooms]);

  const handleInitBooking = async () => {
    const reservation: IPostInitBookingServiceParams = {
      hotel: hotelSlug,
      start_date: searchData.dateRange.startDate?.format("YYYY-MM-DD") || "",
      end_date: searchData.dateRange.endDate?.format("YYYY-MM-DD") || "",
      currency: searchData.currency,
      occupancies: searchData.rooms.reduce((acc: any, val, index) => {
        const occupancy = {
          adult: val.guestAdults,
          child: String(val.guestChildren.length),
          child_ages: val.guestChildren,
          room_id: String(selectedRooms[index]),
          board_id: String(
            selectedArrangements[index][selectedRooms[index]].boardId
          ),
        };
        acc.push(occupancy);
        return acc;
      }, []),
    };

    await initBookingService.execute(reservation).then((res: any) => {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("checkoutSlug", res.slug);
      history.push(`${currentUrl.pathname}${currentUrl.search}`);
    });

    handleStepChange(currentStep + 1); // Navigate to the next step
  };

  const calculateTotalPrice = () => {
    return calculateStayTotalPrice(
      userType!,
      searchResult?.occupancies!,
      selectedRooms,
      selectedArrangements
    );
  };

  const onSelectRoomType = (roomIndex: number) => (roomTypeId: number) => {
    setSelectedRooms((v) => ({ ...v, [roomIndex]: roomTypeId }));
  };

  const Booking = () => (
    <div className="listingSection__wrap lg:border-b  p-0 border-none">
      {searchResult?.occupancies?.map((room: any, index: any) => (
        <div className="bg-white shadow-lg border" key={index}>
          <h2 className="text-lg font-semibold text-[#222] p-4">
            Chambre {index + 1} :{" "}
            {room.pax.adult > 1
              ? `${room.pax.adult} Adultes`
              : `${room.pax.adult} Adulte`}{" "}
            {room?.pax?.child > 0 &&
              (room.pax.child > 1
                ? `et ${room.pax.child} enfants`
                : `et ${room.pax.child} enfant`)}
          </h2>
          {room.room_types.length !== 0 ? (
            <BookingRooms
              isHotelDetails
              hotelDetails={hotelDetails}
              selectedRoomType={selectedRooms?.[index]}
              onSelectRoomType={onSelectRoomType(index)}
              roomIndex={index}
              selectedArrangements={selectedArrangements}
              setSelectedArrangements={setSelectedArrangements}
              roomTypes={room.room_types}
              isIframe
              className="rounded-none"
            />
          ) : (
            <div className="text-base text-red-500">
              Aucune chambre disponible correspondante à votre recherche.
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="relative min-h-screen flex flex-col">
      {/* Booking Summary */}
      <BookingSummary
        currentParams={currentParams}
        searchData={searchData}
        handleStepChange={handleStepChange}
      />

      <div className="flex lg:flex-row flex-col">
        {/* Main Booking Section */}
        <div className="lg:w-[70%] w-full flex-1">
          {searchResult && <Booking />}
        </div>
        {/* Merged SideBar Section */}
        <div className="lg:ml-8 lg:w-[30%] w-full lg:mt-0 mt-8 lg:block flex-grow">
          <div className="lg:sticky lg:top-4 flex flex-col w-full max-w-lg">
            {/* Contenu avec fond blanc */}
            <div className="bg-white p-4 space-y-6 shadow-lg border">
              {/* Titre du Récapitulatif */}
              <h2 className="text-lg font-semibold text-[#222] mb-2">
                Récapitulatif
              </h2>

              <DisplaySideBarBody
                hotel={searchResult!}
                selectedArrangements={selectedArrangements}
                selectedRooms={selectedRooms}
                dividerClassName="w-14 mb-4"
              />

              {/* Conditional Total Display */}
              {searchResult &&
                !(
                  searchResult?.occupancies.length === 1 &&
                  searchResult?.occupancies[0].room_types.length === 0
                ) && (
                  <>
                    <div className="w-full border-t border-neutral-300 dark:border-neutral-600" />
                    <div className="flex justify-between items-center">
                      <div className="text-lg font-semibold">Total</div>
                      <div className="flex flex-col items-end">
                        {calculateTotalPrice().price_without_promotion !==
                          calculateTotalPrice().price && (
                          <div className="text-sm text-red-500 line-through">
                            {calculateTotalPrice().price_without_promotion}{" "}
                            {searchData?.currency}
                          </div>
                        )}
                        <div className="text-xl font-bold text-black">
                          {calculateTotalPrice().price} {searchData?.currency}
                        </div>
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
      {/* Sticky footer for "Suivant" button */}
      <div className="sticky bottom-0 w-full p-4 flex justify-end z-10 mt-5 bg-white">
        {!isStopSale && searchResult && (
          <ButtonPrimary
            className="rounded-none !bg-[#aa8453] w-36"
            onClick={handleInitBooking}
            loading={initBookingService.loading}
          >
            Continuer
          </ButtonPrimary>
        )}
      </div>
    </div>
  );
};

export default Step2;
